import { useEffect, useMemo, useRef, useState } from 'react';
import Accordian from '../../components/Accordian';
import { Text } from '../../components/typography';
import { customColors } from '../../utilities';
import styles from './InspectionMeetingHeader.module.scss';
import { useMutation, useSubscription } from '@apollo/client';
import { loader } from 'graphql.macro';
import VoiceRecorder from '../../components/VoiceRecorder';
import { useModal } from '../../providers/ModalProvider';
import { useVoiceRecorder } from '../../providers/VoiceRecorderProvider';
import { useInspectionMutations } from '../../hooks/inspection/inspection_auxilary';
import { useAIMutations } from '../../hooks/AI/AI_hooks';
import { useCurrentUser } from '../../providers/UserProvider';
import { Form } from 'react-bootstrap';
import Icon from '../../components/Icon';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import { getFullAudioFile } from '../../utilities/inspection';
import { useFileUpload } from '../../hooks/offline-hooks/addFile';

const audioSummarySubscription = loader(
  'src/hooks/AI/summarizeMeetingSubscription.graphql',
);
const updateInspectionMutation = loader('./Inspection.update.graphql');

export default function InspectionMeetingHeader({
  inspection,
  expand,
  onComplete = () => {},
  subscribe,
}) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [info, setInfo] = useState(inspection?.executiveSummary);
  const [shouldSubscribe, setShouldSubscribe] = useState(subscribe);
  const { isAdmin } = useCurrentUser();
  const [isEditing, setIsEditing] = useState(false);
  const [updateInspection] = useMutation(updateInspectionMutation);
  const [waitingForUpload, setWaitingForUpload] = useState(false);
  const { openConfirmationModal } = useModal();
  const {
    stopRecording,
    isRecording,
    audioURL,
    ready,
    setReady,
    fullAudioFileRef,
    audioChunksRef,
  } = useVoiceRecorder();
  const { addAttachment } = useInspectionMutations();
  const { summarizeAudio } = useAIMutations();
  const [editedSummary, setEditedSummary] = useState(info?.summary);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState(inspection?.notes);
  const editingRef = useRef(null);
  const notesRef = useRef(null);
  const online = useOnlineStatus();
  const { addFileToS3 } = useFileUpload();

  useEffect(() => {
    setInfo(inspection?.executiveSummary);
  }, [inspection?.executiveSummary]);

  useEffect(() => {
    setEditedSummary(info?.summary);
  }, [info?.summary]);

  useEffect(() => {
    setNotes(inspection?.notes);
  }, [inspection?.notes]);

  useSubscription(audioSummarySubscription, {
    skip: !shouldSubscribe && !ready && !audioURL?.url,
    variables: { inspectionId: inspection?.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { audioSummarized },
      },
    }) => {
      if (!!audioSummarized?.executiveSummary) {
        setInfo(audioSummarized.executiveSummary);
      }
      setShouldSubscribe(false);
      onComplete();
    },
  });

  useEffect(() => {
    if (!!audioURL?.url && !!ready && !info) {
      addAttachment({
        inspectionId: inspection.id,
        file: { ...audioURL, fileType: 'AUDIO' },
      }).then(() => {
        setReady(false);
      });
      setWaitingForUpload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingForUpload, audioURL, ready, inspection?.id]);

  const isSummarizing = useMemo(() => {
    if (!inspection.attachments.length) return false;
    if (
      inspection.attachments.some(
        (i) => i.fileType === 'AUDIO' && !inspection.executiveSummary,
      )
    ) {
      setShouldSubscribe(true);
      return true;
    }
    return false;
  }, [inspection?.attachments, inspection?.executiveSummary]);

  const content = useMemo(() => {
    if ((shouldSubscribe || isSummarizing) && !isRecording) {
      return (
        <Text size="sm" noMargin>
          Summarizing ...
        </Text>
      );
    }
    if (!!info) {
      return (
        <div className={styles.container}>
          <div className={styles.header}>
            <Text noMargin weight="semiBold">
              Summary
            </Text>
            <div className={styles.flexRow}>
              <Icon
                hover
                style={{ color: customColors.BLUE }}
                onClick={() => {
                  openConfirmationModal({
                    title: 'Refresh Summary',
                    variant: 'warning',
                    description:
                      'Refreshing will generate a new summary for your meeting and erase the current summary. This action cannot be undone',
                    onSubmit: () => {
                      setShouldSubscribe(true);
                      summarizeAudio({ inspectionId: inspection.id });
                    },
                  });
                }}
              >
                refresh
              </Icon>
              {inspection?.isDraft && !isEditing && (
                <Icon
                  hover
                  onClick={() => setIsEditing(true)}
                  style={{ opacity: 0.5 }}
                >
                  edit
                </Icon>
              )}
            </div>
          </div>
          {isEditing ? (
            <Form.Control
              ref={editingRef}
              as="textarea"
              rows={20}
              className={styles.prewrap}
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              onBlur={() => {
                const executiveSummary = {
                  transcript: info?.transcript,
                  segments: info?.segments,
                  summary: editedSummary,
                };
                updateInspection({
                  variables: { id: inspection?.id, executiveSummary },
                });
                setIsEditing(false);
              }}
            />
          ) : (
            <Text noMargin className={styles.prewrap}>
              {editedSummary || info?.summary || 'No Summary Found'}
            </Text>
          )}
          {isAdmin && (
            <Text
              hover
              color="secondary"
              noMargin
              size="xs"
              onClick={() => setShowTranscript(!showTranscript)}
            >
              {`${!!showTranscript ? 'Hide' : 'View'} Transcript`}
            </Text>
          )}
          {!!showTranscript && isAdmin && !!inspection && (
            <div>
              {!!inspection.audioURL && (
                <div className={styles.center}>
                  <audio controls src={inspection.audioURL || audioURL} />
                </div>
              )}
              {!!info?.segments?.length ? (
                <div className={styles.segments}>
                  {(() => {
                    return info.segments.map(({ start, text }, idx) => {
                      return (
                        <div className={styles.segment} key={idx}>
                          <Text size="sm" noMargin>{`• ${text}`}</Text>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className={styles.transcript}>
                  <Text>{info?.transcript || 'No Transcript Found'}</Text>
                </div>
              )}
            </div>
          )}
        </div>
      );
    } else if (inspection.isDraft) {
      return (
        <div className={styles.bottom}>
          <VoiceRecorder
            allowNotes={false}
            disabled={!inspection?.isDraft || !online}
            className={styles.voiceRecorder}
            onStart={() => setShouldSubscribe(true)}
            onStop={() => {
              openConfirmationModal({
                title: 'Stop Recording',
                variant: 'warning',
                description: `Are you sure you would like to stop recording? This will end your meeting transcript.`,
                onSubmit: async () => {
                  if (isRecording) {
                    await stopRecording();
                    if (audioChunksRef?.current) {
                      audioChunksRef.current = [];
                    }
                    const fullAudioFileUrl = await getFullAudioFile(
                      fullAudioFileRef,
                      addFileToS3,
                    );
                    await updateInspection({
                      variables: {
                        id: inspection.id,
                        audioURL: fullAudioFileUrl,
                      },
                    });
                    await summarizeAudio({
                      inspectionId: inspection.id,
                    });
                    setShouldSubscribe(true);
                  }
                  setWaitingForUpload(true);
                },
              });
            }}
          />
        </div>
      );
    } else {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    audioChunksRef,
    audioURL,
    editedSummary,
    fullAudioFileRef,
    info,
    inspection,
    isAdmin,
    isEditing,
    isRecording,
    online,
    showTranscript,
    stopRecording,
    waitingForUpload,
    shouldSubscribe,
  ]);

  useEffect(() => {
    if (!!isEditing && editingRef.current) {
      editingRef.current.focus();
    }
    if (!!isEditingNotes && notesRef.current) {
      notesRef.current.focus();
    }
  }, [isEditing, isEditingNotes]);

  return (
    <Accordian
      backgroundColor={customColors.DARK_GRAY}
      textColor="white"
      iconProps={{ color: 'white' }}
      size="md"
      messageVariant="white"
      title="Meeting Minutes"
      icon="audio_file"
      accordianSize={true}
      expand={expand}
      message={
        !!inspection.executiveSummary || !!inspection.notes
          ? 'Complete'
          : 'None'
      }
    >
      {(!!inspection?.notes || !!inspection.isDraft) && (
        <div className={styles.notes}>
          <div className={styles.header}>
            <Text noMargin weight="semiBold">
              Meeting Notes
            </Text>
            {inspection?.isDraft && !isEditingNotes && (
              <Icon
                hover
                style={{ opacity: 0.5 }}
                onClick={() => {
                  setNotes(inspection?.notes);
                  setIsEditingNotes(true);
                }}
              >
                edit
              </Icon>
            )}
          </div>
          {isEditingNotes ? (
            <Form.Control
              ref={notesRef}
              as="textarea"
              value={notes}
              rows={6}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => {
                setIsEditingNotes(false);
                updateInspection({
                  variables: {
                    id: inspection.id,
                    notes,
                  },
                });
              }}
            />
          ) : (
            <Text noMargin className={styles.prewrap}>
              {inspection?.notes}
            </Text>
          )}
        </div>
      )}
      {content}
    </Accordian>
  );
}
